

import NationalNoticeService from "services/NationalNoticeService";
import useSnackbar from "hooks/useSnackbar";
import iconWorksReducer from "libs/iconWorksReducer";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { connect } from "react-redux";
import Tr from "translations/Translations";
import contractPerformingNoticeValidation from "./contractPerformingNoticeValidation";
import _ from "lodash";
import Loading from "components/Loading";
import Section from "pages/purchaser/MyProcedure/scenes/part2/components/Section";
import FormTranslationContext from "context/FormTranslationContext";
import Form from "components/Form";
import NationalNoticeStatus from "../constants/NationalNoticeStatus";
import { contractPerformingNoticeStepsOrder } from "./contractPerformingNoticeSteps";
import FormNavigator from "../components/FormNavigator";
import { Collapse, Typography } from "@material-ui/core";
import BoldTypography from "components/BoldTypography";
import scrollTo from "libs/scrollTo";
import BasicData from "./components/BasicData";
import PurchaserData from "./components/PurchaserData";
import AdditionalInfoData from "../resultNotice/components/AdditionalInfoData";
import NoticeProcedureSummary from "./components/NoticeProcedureSummary";
import ContractData from "./components/ContractData";
import ContractProgress from "./components/ContractProgress";
import GridBlock from "components/GridBlock";
import Button from "components/Button";
import Confirm from "components/popup/confirm/Confirm";

function getKey(id) {
    const split = id.split(".").filter(x => !/^\d+$/.test(x));
    split[0] = 'contractPerformingNotice';
    return split.join(".");
}

const useStyle = makeStyles(theme => ({
    collapse: {
        marginTop: theme.spacing(3)
    },
    info: {
        textAlign: "center",
        fontWeight: "bold",
        marginTop: theme.spacing(2),
        cursor: "pointer"
    }
}))


function ContractPerformingNoticeForm(
    {
        demandId,
        agreementId,
        noticeId,
        positionIds,
        autoCreateVersion
    }
) {

    const classes = useStyle();
    const FORM_ID = "ContractPerformingNotice";
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [loaded, setLoaded] = useState(false)
    const [initData, setInitData] = useState(null);
    const [dictionaries, setDictionaries] = useState(null);
    const [translations, setTranslations] = useState(null);
    const autoCreateTriggeredRef = useRef(false);

    const [buttonWorks, dispatchButtonWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        save: false,
        draft: false
    });

    const formTr = (id) => {
        const key = getKey(id);
        return translations.labels[key] || key;
    }
    const formTip = (id) => {
        const key = getKey(id);
        return translations.tips[key];
    }

    const getDict = (id) => {
        return dictionaries[id].items;
    }

    useEffect(() => {
        const loadNoticePromise = noticeId
            ? NationalNoticeService.getContractPerformingNoticeById(agreementId, noticeId)
            : NationalNoticeService.getContractPerformingNotice(agreementId, positionIds);

        Promise.all([
            loadNoticePromise,
            NationalNoticeService.getContractPerformingNoticeDictionaries(agreementId),
            NationalNoticeService.getContractPerformingNoticeTranslations(agreementId)])
            .then(values => {
                setInitData(values[0]);
                setDictionaries(values[1]);
                setTranslations(values[2]);
            }).then(() => setLoaded(true))


    }, [agreementId, noticeId, positionIds])

    const handleSave = (formikProps) => {
        formikProps.setFieldValue("_editable", false);
        dispatchButtonWorks({ type: 'draft', works: true });
        NationalNoticeService.saveContractPerformingNoticeDraft(agreementId, formikProps.values)
            .then((resp) => {
                if (!!resp) {
                    enqueueSuccessSnackbar(Tr("saved_draft"));
                    setInitData(x => ({ ...x, ...resp }))
                } else {
                    throw new Error();
                }
            })
            .catch(() => enqueueErrorSnackbar(Tr("unknown_error_message")))
            .finally(() => {
                formikProps.setFieldValue("_editable", true);
                dispatchButtonWorks({ type: 'draft', works: false });
            })
    }

    const handleSubmit = (formikProps) => {
        (async () => {
            try {
                const casted = await contractPerformingNoticeValidation().validate(formikProps.values, { abortEarly: false })
                formikProps.setFieldValue("_editable", false)
                dispatchButtonWorks({ type: 'save', works: true });
                NationalNoticeService.saveContractPerformingNotice(agreementId, casted)
                    .then((resp) => {
                        if (resp.success) {
                            enqueueSuccessSnackbar(Tr("saved"))
                            setInitData(x => ({ ...x, ...resp.result }))
                        } else {
                            const keys = Object.keys(resp.errors);
                            let message;
                            if (Array.isArray()) {
                                message = resp.errors[keys[0]][0];
                            } else if (_.isString(resp.errors[keys[0]])) {
                                message = resp.errors[keys[0]];
                            } else {
                                message = Tr("unknown_error_message")
                            }

                            navigateToError(keys[0], message, formikProps, true)
                        }
                    })
                    .catch(() => enqueueErrorSnackbar(Tr("unknown_error_message")))
                    .finally(() => {
                        formikProps.setFieldValue("_editable", true);
                        dispatchButtonWorks({ type: 'save', works: false });
                    })
            } catch (e) {
                navigateToError(e.inner[0].path, e.inner[0].message, formikProps);
            }
        })()
    }

    const deleteLastVersion = () => {
        setConfirmRemove(false);
        setLoaded(false);
        setInitData(null);

        (async () => {
            try {
                await NationalNoticeService.deleteLastContractPerformingNoticeVersion(agreementId, positionIds);
                const resp = await NationalNoticeService.getContractPerformingNotice(agreementId, positionIds);
                setInitData(resp);
            } catch (e) {
            }
            setLoaded(true);
        })();
    }

    const navigateToError = (errorpath, message, formikProps, setError) => {

        const formikPath = errorpath.replaceAll("[", ".").replaceAll("]", "");
        if (setError) {
            formikProps.setFieldTouched(formikPath, true);
            formikProps.setFieldError(formikPath, message);
        }

        const path = errorpath.split(".");
        enqueueErrorSnackbar(<span><strong>{message}</strong>{": " + formTr(path.map(x => x.replace(/\[(.*)\]/g, '')).join("."))}</span>)
        formikProps.setFieldValue("step", path[1]);
        if (path.length > 2) {
            if (path[1] === "noticeProcedureSummary" && path[2] === "noticeObjectData" && path[3]?.startsWith("divided[")) {
                formikProps.setFieldValue("noticeObjectDealing", parseInt(path[3].replace(/\D/g, '')));
            }
        }
        setTimeout((p) => scrollTo('[data-id="' + p + '"]', 100), 100, formikPath)
    }

    const createNewVersion = useCallback(() => {
        setLoaded(false);
        setInitData(null);
        NationalNoticeService.createNewContractPerformingNoticeVersion(agreementId, positionIds).then((resp) => setInitData(resp)).then(() => setLoaded(true));
    }, [agreementId, positionIds]);

    useEffect(() => {
        if (!autoCreateVersion || !loaded || !initData || autoCreateTriggeredRef.current) return;
        if (initData.status !== NationalNoticeStatus.PUBLISHED) return;

        autoCreateTriggeredRef.current = true;
        createNewVersion();
    }, [autoCreateVersion, loaded, initData, createNewVersion]);

    if (!loaded) return <Loading />

    return (
        <Section title={Tr(!initData.updateNotice ? "bzp_contract_performing_notice" : "bzp_contract_performing_notice_update")}>
            <FormTranslationContext.Provider value={{
                formTr, formTip, getDict, loadDynamicDictionaries: undefined
            }}>
                <Form
                    id={FORM_ID}
                    validateOnMount
                    onSubmit={() => { }}
                    validationSchema={contractPerformingNoticeValidation()}
                    initialValues={{
                        _editable: initData.editable && (initData.status === NationalNoticeStatus.DRAFT || initData.status === NationalNoticeStatus.COMPLETED || initData.status === NationalNoticeStatus.PUBLICATION_ERROR),
                        _passive: initData.passive,
                        _onlyNotice: initData.onlyNotice,
                        _updateNotice: initData.updateNotice,
                        form: {
                            positionIds: initData.form.positionIds || [],
                            basicData: initData.form.basicData || {},
                            purchaserData: initData.form.purchaserData || {},
                            noticeProcedureSummary: initData.form.noticeProcedureSummary || {},
                            contractData: initData.form.contractData || {},
                            contractProgress: initData.form.contractProgress || {},
                            additionalInfoData: initData.form.additionalInfoData || {},
                        },
                        step: 'basicData',
                        noticeObjectDealing: -1,
                        contractUpdateDealing: -1,
                    }}
                >
                    {
                        (formikProps) => {
                            const step = formikProps.values.step
                            const statusTranslationKey = initData.status === NationalNoticeStatus.PUBLICATION_ERROR
                                ? "ezamowienia_state_contract_performing_notice_publication_error"
                                : "ezamowienia_state_" + initData.status.toLowerCase();
                            return (
                                <>
                                    {!!initData.ezamowieniaErrors && <>
                                        <BoldTypography>{Tr('bzp_last_ezamowienia_errors')}</BoldTypography>
                                        <ul>
                                            {
                                                Object.keys(initData.ezamowieniaErrors).map(x => (<ul key={x}>
                                                    <li>{x}
                                                        <ul>
                                                            {initData.ezamowieniaErrors[x].map(e => (<li key={e}>{e}</li>))}
                                                        </ul>
                                                    </li>
                                                </ul>))
                                            }
                                        </ul>
                                    </>}

                                    <Typography id="form-top-scroll-to" variant="body2"
                                        align='right'><strong>{Tr('current_state')}:</strong> {Tr(statusTranslationKey)}
                                    </Typography>
                                    <FormNavigator
                                        formikProps={formikProps}
                                        id="step"
                                        items={contractPerformingNoticeStepsOrder}
                                        formTr={formTr}
                                        trPrefix="contractPerformingNotice."
                                        onSubmit={() => handleSubmit(formikProps)}
                                        onSave={() => handleSave(formikProps)}
                                        onCreateNewVersion={initData.status === NationalNoticeStatus.PUBLISHED ? createNewVersion : undefined}
                                        buttonWorks={buttonWorks}
                                        doAutoSave={initData.editable && initData.status === NationalNoticeStatus.DRAFT}
                                    />
                                    <Collapse className={classes.collapse} in={step === "basicData"} unmountOnExit>
                                        <BasicData formikProps={formikProps} id="form.basicData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "purchaserData"} unmountOnExit>
                                        <PurchaserData formikProps={formikProps} id="form.purchaserData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "noticeProcedureSummary"} unmountOnExit>
                                        <NoticeProcedureSummary formikProps={formikProps} id="form.noticeProcedureSummary"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "contractData"} unmountOnExit>
                                        <ContractData formikProps={formikProps} id="form.contractData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "contractProgress"} unmountOnExit>
                                        <ContractProgress formikProps={formikProps} id="form.contractProgress"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "additionalInfoData"} unmountOnExit>
                                        <AdditionalInfoData formikProps={formikProps} id="form.additionalInfoData"/>
                                    </Collapse>

                                    <GridBlock justify="flex-end">
                                        {initData.editable && initData.modifiedDate != null && (initData.status === NationalNoticeStatus.DRAFT || initData.status === NationalNoticeStatus.COMPLETED) &&
                                            <Button variant="outlined" onClick={() => setConfirmRemove(true)}>{Tr("bzp_remove_last_version")}</Button>
                                        }
                                    </GridBlock>
                                    {initData.status === NationalNoticeStatus.COMPLETED &&
                                        <Typography
                                            className={classes.info}>{Tr("bzp_go_to_agreement_notice_info")}
                                            <Button variant="text"
                                                    onClick={() => {
                                                        if (initData.statusTabUrl) {
                                                            window.location.href = initData.statusTabUrl;
                                                        }
                                                    }}>
                                                {Tr("bzp_status_tab")}
                                            </Button> </Typography>}


                                    <Confirm
                                        confirmText='bzp_remove_last_version_confirm'
                                        onCancel={() => setConfirmRemove(false)}
                                        onConfirm={deleteLastVersion}
                                        open={confirmRemove}
                                    />
                                </>
                            )
                        }
                    }
                </Form>
            </FormTranslationContext.Provider>
        </Section>
    )

}

export default connect((state) => ({
    demandHeader: state.resources.demandHeader
}), {})(ContractPerformingNoticeForm);